import { useState } from 'react'
import { Trans } from 'react-i18next'

import { AppBreakpoint } from '@makyoapp/makyo-sdk/models/AppBuilder'
import { IActionFormProps } from '@makyoapp/makyo-common'
import { ModalWindow16, Screen24 } from '@makyoapp/makyo-icon/icon'
import { Button, ButtonVariant, Divider, Field, Label, SingleIconSelect, Slider } from '@makyoapp/makyo-ui'

import { useForm } from '@/lib/use-form'
import { BUILDER_DEVICES } from '@/shared/builder-constants'
import {
  DEFAULT_OVERLAY_PLACEMENT,
  DEFAULT_OVERLAY_SHAPE,
  DEFAULT_OVERLAY_SIZE,
  formatDrawerSize,
  getDirectionLabel,
  getDrawerSize,
  getOverlaySizeLabel,
  overlayPlacements,
} from '@/shared/overlay'
import { isEmpty } from '@/shared/utils'
import { DeviceTab } from '@/components/DeviceTab'

const screenTypeOptions = [
  {
    label: 'Page',
    value: 'page',
    icon: <Screen24 width={16} height={16} />,
  },
  {
    label: 'Overlay',
    value: 'overlay',
    icon: <ModalWindow16 />,
  },
]

const defaultOverlaySettings = {
  closeBehavior: 'direct',
  placement: DEFAULT_OVERLAY_PLACEMENT,
  size: DEFAULT_OVERLAY_SIZE,
  shape: DEFAULT_OVERLAY_SHAPE,
  opacity: 85,
}

export default function ActFormShowDetailScreen({ onSubmit, onDismiss, initialData }: IActionFormProps) {
  const [device, setDevice] = useState<AppBreakpoint>('phone')
  const devices = Object.keys(BUILDER_DEVICES) as AppBreakpoint[]

  const { form, onSubmit: onFormSubmit } = useForm({
    defaultValues: {
      screenType: initialData?.screenType || 'overlay',
      overlaySettings: initialData?.overlaySettings || defaultOverlaySettings,
    },
    validate(values, setError) {},
  })

  const handleSubmit = onFormSubmit((data) => {
    let actionSettings = data

    if (actionSettings.screenType === 'page') {
      actionSettings.overlaySettings = undefined
    }

    onSubmit(actionSettings)
  })

  const isOverlay = form.getValues('screenType') === 'overlay'

  const value = form.getValues('screenType')
  const invalidForm = isEmpty(value) || !!form.errors

  const handleChangeOverlaySettings = (key: string, value: any) => {
    const currentSettings = form.getValues('overlaySettings') || defaultOverlaySettings

    if (key === 'placement' || key === 'size' || key === 'shape') {
      form.setValues('overlaySettings', {
        ...currentSettings,
        [key]: {
          ...currentSettings[key],
          [device]: value,
        },
      })
    } else {
      form.setValues('overlaySettings', {
        ...currentSettings,
        [key]: value,
      })
    }
  }

  const currentPlacement = form.getValues('overlaySettings')?.placement[device]
  const isCenter = currentPlacement === 'center'
  const currentSize = form.getValues('overlaySettings')?.size[device] || DEFAULT_OVERLAY_SIZE[device]
  const currentOpacity = form.getValues('overlaySettings')?.opacity || 85

  return (
    <div>
      <div className='p-4'>
        <Field>
          <Label required>Screen Type</Label>
          <div className='flex-1 flex items-center gap-2'>
            {screenTypeOptions.map((opt) => (
              <Button
                key={opt.value}
                onClick={() => {
                  form.setValues('screenType', opt.value)

                  if (opt.value === 'page') {
                    form.setValues('overlaySettings', undefined)
                  }
                  if (opt.value === 'overlay' && !form.getValues('overlaySettings')) {
                    form.setValues('overlaySettings', defaultOverlaySettings)
                  }
                }}
                variant={form.getValues('screenType') === opt.value ? ButtonVariant.Normal : ButtonVariant.Gray}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </Field>

        {isOverlay && (
          <>
            <Divider className='border-dashed' />

            <div className='flex overflow-hidden rounded-sm'>
              {devices.map((d) => (
                <DeviceTab key={d} device={d} isActive={device === d} onClick={() => setDevice(d)} />
              ))}
            </div>

            {/* Adaptive Settings */}
            <div className='mt-4'>
              <Field>
                <Label>Direction</Label>
                <div>
                  <SingleIconSelect
                    options={overlayPlacements.map((p) => ({
                      id: p.value,
                      icon: p.icon,
                      title: getDirectionLabel(p.value),
                    }))}
                    selected={currentPlacement}
                    onClick={(opt) => handleChangeOverlaySettings('placement', opt.id)}
                  />
                </div>
              </Field>

              <Field>
                <Label>{getOverlaySizeLabel(currentPlacement)}</Label>
                <div className='w-[50%]'>
                  <Slider
                    values={[getDrawerSize(currentSize).v1]}
                    max={100}
                    min={30}
                    step={1}
                    onChange={(val) => {
                      handleChangeOverlaySettings('size', formatDrawerSize('side', currentSize, `${val[0]}%`))
                    }}
                    renderLabel={(val) => `${val}%`}
                  />
                </div>
              </Field>

              {isCenter && (
                <Field>
                  <Label>Height</Label>
                  <div className='w-[50%]'>
                    <Slider
                      values={[getDrawerSize(currentSize).v2]}
                      max={100}
                      min={30}
                      step={1}
                      onChange={(val) => {
                        handleChangeOverlaySettings('size', formatDrawerSize('center', currentSize, `${val[0]}%`))
                      }}
                      renderLabel={(val) => `${val}%`}
                    />
                  </div>
                </Field>
              )}
            </div>

            {/* Non-adaptive Settings */}
            <Divider className='my-4' />
            <div>
              <Field>
                <Label>Backdrop Transparency</Label>
                <div className='w-[50%]'>
                  <Slider
                    values={[currentOpacity]}
                    max={100}
                    min={30}
                    step={1}
                    onChange={(val) => {
                      handleChangeOverlaySettings('opacity', val[0])
                    }}
                    renderLabel={(val) => `${val}%`}
                  />
                </div>
              </Field>
            </div>
          </>
        )}
      </div>

      <div className='px-4 py-2 border-t bdr flex justify-between items-center'>
        <Button variant={ButtonVariant.Outlined} onClick={onDismiss}>
          <Trans>Cancel</Trans>
        </Button>
        <Button onClick={handleSubmit} disabled={invalidForm}>
          <Trans>Save</Trans>
        </Button>
      </div>
    </div>
  )
}
